import React, { useState } from "react";

import {
  Alert, // Added Alert
  Box,
  Chip,
  Fab,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteScheduleRequest,
  fetchSchedules,
  postSchedules,
} from "../../redux/reducers/user/slice";
import DaysTabs from "../DaysTabs/DaysTabs";
import SaveIcon from "@mui/icons-material/Save";
import AutoDeleteIcon from "@mui/icons-material/AutoDelete";
import EmailIcon from "@mui/icons-material/Email";
import { AnimatePresence, motion } from "framer-motion";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import ScheduleIcon from "@mui/icons-material/Schedule";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import TimeZoneList from "../TimeZoneList";
import Lottie from "lottie-react";
import Loading from "../../assets/lottie/animation.json";
import { useNavigate } from "react-router-dom";
import { abbreviateDay, countScheduledHours, validateEmail } from "../../utils";
import ListSelectCustom from "../ListSelectCustom/ListSelectCustom";

const MotionGrid = motion(Grid);

const options = {
  loop: true,
  autoplay: true,
  style: { width: 125, height: 125, margin: "auto" },
  animationData: Loading,
};

export default function ScheduleTab() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    schedules,
    mySchedules,
    schedules_loading,
    schedulesFetched,
    post_schedules_loading,
    delete_schedules_loading,
    post_schedules_success,
    isClientFetched,
    errorOnAuthRequest,
    plan, // Used for the alert condition
    usedSchedules
  } = useSelector((state) => state.user);
  const { lists } = useSelector((state) => state.trello);
  const [listName, setListName] = React.useState([]);
  const [value, setValue] = useState(1);
  const [email, setEmail] = useState("");
  const [addNewSchedule, setAddNewSchedule] = useState(false);
  const [finalList, setFinalList] = React.useState([]);
  const [fileType, setFileType] = React.useState("xlsx");
  const [finalHourList, setFinalHourList] = React.useState([]);
  const [weekdaysArr, setWeekdaysArr] = React.useState([]);
  const [timeZone, setTimeZone] = React.useState("");
  const totalSchedules = countScheduledHours(mySchedules);
  const maxSchedules = plan?.numberMaxTarefas || 0;
  const currentTotalSchedules = finalHourList.length + totalSchedules;
  const btnValidation =
    !validateEmail(email) ||
    !finalHourList.length ||
    !value ||
    !finalList.length ||
    post_schedules_loading ||
    schedules_loading ||
    currentTotalSchedules > maxSchedules;

  React.useEffect(() => {
    if (post_schedules_success) {
      const newArr = schedules?.map((s) => {
        return { ...s, hours: [] };
      });

      setValue(1);
      setAddNewSchedule(false);
      setWeekdaysArr(newArr);
    }

    setWeekdaysArr(
      schedules?.map((s) => {
        return { ...s, hours: [] };
      })
    );
  }, [post_schedules_success, schedules]);

  React.useEffect(() => {
    const transformedArray = weekdaysArr.reduce((result, day) => {
      const hourObjects = day.hours?.map((hour) => ({
        escalaId: day.id,
        hourAgenda: hour,
      }));
      return [...result, ...hourObjects];
    }, []);

    setFinalHourList(transformedArray);
  }, [weekdaysArr, schedules]);

  React.useEffect(() => {
    if (listName.length > 0) {
      const updatedFinalList = listName
        ?.map((item) => {
          const selectedList = lists.find((list) => list.name === item);

          return selectedList
            ? {
                idListaId: selectedList.value,
                nameLista: selectedList.name,
              }
            : null;
        })
        .filter(Boolean);

      setFinalList(updatedFinalList);
    }
  }, [listName, lists]);

  React.useEffect(() => {
    if (!schedules?.length && !schedules_loading && !schedulesFetched) {
      dispatch(fetchSchedules());
    }
  }, [dispatch, schedules, schedules_loading, schedulesFetched]);

  const handleSaveSchedule = () => {
    if (
      !validateEmail(email) ||
      !finalHourList.length ||
      !value ||
      !finalList.length ||
      usedSchedules >= maxSchedules || // Using >= for clarity
      currentTotalSchedules > maxSchedules
    ) {
      return;
    }
    // This existing logic already checks for !plan and navigates
    if (isClientFetched && !plan) {
      return navigate("/billing");
    }
    const data = {
      layout: finalList,
      agenda: finalHourList,
      TimeZoneCustomerId: timeZone,
      textEmail: email,
      typeExportacao: fileType
    };
    dispatch(postSchedules(data));
  };

  const handleSelectTimeZone = (e) => {
    setTimeZone(e);
  };

  const handleDeleteSchedule = (id) => {
    if (delete_schedules_loading) {
      return;
    }
    dispatch(deleteScheduleRequest(id));
  };

  const sortSchedules = (schedulesToSort) => {
    if (!Array.isArray(schedulesToSort)) return []; // Add guard for undefined/null
    return schedulesToSort.map((schedule) => ({
      ...schedule,
      agendaEscalaGetViewModels: [...(schedule.agendaEscalaGetViewModels || [])] // Add guard for undefined/null
        .sort((a, b) => {
          if (a.escalaGetViewModel.id !== b.escalaGetViewModel.id) {
            return a.escalaGetViewModel.id - b.escalaGetViewModel.id;
          }
          return a.hourAgenda.localeCompare(b.hourAgenda);
        }),
    }));
  };

  const sortedSchedules = sortSchedules(mySchedules);

  // Condition for displaying the alert, using isClientFetched for robustness as plan might be initially undefined.
  // User confirmed: "O alerta deve ser exibido quando o usuário não tiver um plano ativo (plan for nulo/falso)"
  const displayPlanAlert = isClientFetched && !plan;

  return (
    <>
      {/* Mensagem de plano expirado */}
      {displayPlanAlert && (
        <Grid container sx={{ mb: 2, mt:1 }}> 
          <Grid item xs={12}>
            <Alert
              severity="warning"
              variant="filled"
              sx={{
                display: "flex",
                alignItems: "center",
                "& .MuiAlert-message": {
                  display: "flex",
                  alignItems: "center",
                  padding: 0,
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, ml: 1 }}>
                You have no active plans and your trial period has ended.
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      )}
      <AnimatePresence>
        {!addNewSchedule && (
          <MotionGrid
            initial={{ x: "-100vw" }}
            animate={{
              x: 0,
              transition: { type: "spring", mass: 0.4, damping: 8 },
            }}
            exit={{ opacity: 0, scale: 0.5, transition: { delay: 0.1 } }}
            container
            mt={1}
          >
            <MotionGrid item xs={12}>
              <MotionGrid
                item
                xs={12}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    height: 400,
                    p: 1,
                    borderRadius: 3,
                    borderBottom: "1px solid #e1e1e1",
                    overflowY: "scroll",
                    "&::-webkit-scrollbar": {
                      width: "12px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#008c23",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#008c2370",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#e1e1e1",
                    },
                    "&::-webkit-scrollbar-button": {
                      display: "none",
                    },
                  }}
                >
                  <MotionGrid rowGap={1} container>
                    {mySchedules && mySchedules.length > 0 && // Added check for mySchedules existence
                      sortedSchedules
                        ?.map((s, idx) => (
                          <MotionGrid
                            key={s.id} // Added key for list items
                            item
                            exit={{
                              opacity: 0,
                              scale: 0.5,
                              transition: { delay: 0.1 },
                            }}
                            xs={12}
                            display="flex"
                            alignItems="center"
                            sx={{
                              bgcolor: idx % 2 === 0 ? "#DCDCDC" : "#FFF",
                              maxWidth: "100%",
                              border: "2px solid black",
                              borderRadius: 2,
                              mb: 1 
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              sx={{ flexWrap: "wrap", width: "100%" }}
                            >
                              <MotionGrid xs={12} item>
                                <Typography
                                  variant="overline"
                                  display="block"
                                  sx={{
                                    p: 0.5,
                                    fontWeight: "900",
                                    textAlign: "center",
                                    borderBottom: "1px solid black",
                                  }}
                                >
                                  Schedule #{s.id}
                                </Typography>
                              </MotionGrid>
                              <MotionGrid container xs={12}>
                                <MotionGrid
                                  sx={{ borderRight: "1px solid black" }}
                                  xs={6}
                                  item
                                >
                                  <Typography
                                    variant="overline"
                                    display="block"
                                    sx={{
                                      p: 0.5,
                                      fontWeight: "900",
                                      textAlign: "center",
                                      borderBottom: "1px solid black",
                                    }}
                                  >
                                    Scheduled times
                                  </Typography>
                                </MotionGrid>
                                <MotionGrid
                                  sx={{ borderRight: "1px solid black" }}
                                  xs={3}
                                  item
                                >
                                  <Typography
                                    variant="overline"
                                    display="block"
                                    sx={{
                                      p: 0.5,
                                      fontWeight: "900",
                                      textAlign: "center",
                                      borderBottom: "1px solid black",
                                    }}
                                  >
                                    Selected Lists
                                  </Typography>
                                </MotionGrid>
                                <MotionGrid xs={3} item>
                                  <Typography
                                    variant="overline"
                                    display="block"
                                    sx={{
                                      p: 0.5,
                                      fontWeight: "900",
                                      textAlign: "center",
                                      borderBottom: "1px solid black",
                                    }}
                                  >
                                    Actions
                                  </Typography>
                                </MotionGrid>

                                <MotionGrid
                                  sx={{ borderRight: "1px solid black", p:0.5 }}
                                  item
                                  xs={6}
                                >
                                  {s.agendaEscalaGetViewModels?.map(
                                    (weekday) => (
                                      <Chip
                                        key={`${s.id}-${weekday.escalaGetViewModel.id}-${weekday.hourAgenda}`} // Enhanced key
                                        icon={<ScheduleIcon />}
                                        sx={{ m: 0.8 }}
                                        label={`${abbreviateDay(
                                          weekday.escalaGetViewModel.name
                                        )} at ${weekday.hourAgenda}`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )
                                  )}
                                </MotionGrid>
                                <MotionGrid
                                  sx={{ borderRight: "1px solid black", p:0.5 }}
                                  item
                                  xs={3}
                                >
                                  {s.layoutGetViewModels?.map((list) => (
                                    <Chip
                                      key={`${s.id}-${list.idListaId}`} // Enhanced key
                                      icon={<MenuOutlinedIcon />}
                                      sx={{ m: 0.8 }}
                                      label={list.nameLista}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </MotionGrid>
                                <MotionGrid item xs={3}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center", 
                                      flexDirection: "column",
                                      p: 1,
                                      height: "100%"
                                    }}
                                  >
                                    <Fab
                                      size="small"
                                      sx={{
                                        backgroundColor: "#008c23",
                                        color: "white",
                                        "&:hover": {
                                          backgroundColor: "#008c2390",
                                        },
                                      }}
                                      aria-label="delete schedule"
                                      disabled={delete_schedules_loading}
                                      onClick={() => handleDeleteSchedule(s.id)}
                                    >
                                      <AutoDeleteIcon />
                                    </Fab>
                                    <Typography
                                      variant="caption"
                                      sx={{ mt: 1 }}
                                    >
                                      Delete Schedule
                                    </Typography>
                                  </Box>
                                </MotionGrid>
                              </MotionGrid>
                              <MotionGrid xs={12} item>
                                <Typography
                                  variant="overline"
                                  display="block"
                                  sx={{
                                    p: 0.5,
                                    fontWeight: "900",
                                    textAlign: "center",
                                    borderTop: "1px solid black",
                                  }}
                                >
                                  Recipient: {s.textEmail} <EmailIcon sx={{verticalAlign: "middle"}} />
                                </Typography>
                              </MotionGrid>
                            </Box>
                          </MotionGrid>
                        ))
                        .reverse()}

                    {!errorOnAuthRequest &&
                      mySchedules && mySchedules.length === 0 && // Added check for mySchedules existence
                      isClientFetched && (
                        <Typography variant="h5" sx={{m:"auto", p:2}}>
                          You don't have any schedule yet.
                        </Typography>
                      )}

                    {errorOnAuthRequest && isClientFetched && (
                      <Typography variant="h5" sx={{m:"auto", p:2, color: "error.main"}}>
                        An error occurred while fetching the data. Please try
                        again later.
                      </Typography>
                    )}

                    {schedules_loading && !schedulesFetched && (
                      <div style={{width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
                        <Lottie {...options} />
                      </div>
                    )}
                  </MotionGrid>
                </Box>
              </MotionGrid>
            </MotionGrid>
            <MotionGrid
              item
              xs={12}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 2 
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "33%",
                }}
              ></Box>
              <Box
                sx={{
                  p: 2,
                  pb: 0,
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                  width: "33%",
                }}
              >
                <Fab
                  disabled={ 
                    displayPlanAlert || // Added this condition
                    !schedulesFetched ||
                    (schedulesFetched && schedules && schedules.length === 0) || // Check schedules template existence
                    usedSchedules >= maxSchedules || // Check if max schedules already used
                    (mySchedules && mySchedules.length >= maxSchedules) // Alternative check based on mySchedules count
                  }
                  size="small"
                  sx={{
                    backgroundColor: "#008c23",
                    color: "white",
                    "&:hover": { backgroundColor: "#008c2390" },
                  }}
                  aria-label="add new schedule"
                  onClick={() => setAddNewSchedule(true)}
                >
                  <AlarmAddIcon />
                </Fab>
                <Typography variant="button" sx={{ mt: 1 }}>
                  Add new schedule
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexWrap: "wrap",
                  ml: "auto",
                  textAlign: "right", 
                  "& > :not(style)": {
                    m: 0.5, 
                    height: 25,
                  },
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 300 }}> 
                  Used schedules: <strong>{usedSchedules}</strong>
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 300 }}> 
                  Max schedules: <strong>{maxSchedules}</strong>
                </Typography>
              </Box>
            </MotionGrid>
            <MotionGrid
              item
              xs={12}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 1 
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                {currentTotalSchedules > maxSchedules && !displayPlanAlert && (
                  <Typography color="error">
                    You have exceeded the maximum limit of schedules.
                  </Typography>
                )}
              </Box>
            </MotionGrid>
          </MotionGrid>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {addNewSchedule && (
          <MotionGrid
            container
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 0.5 } }}
            exit={{ opacity: 0, scale: 0.5, transition: { delay: 0.1 } }}
            mt={2}
          >
            <MotionGrid item xs={12}>
              <MotionGrid
                item
                xs={12}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <DaysTabs
                  value={value}
                  selectedHour={weekdaysArr}
                  setValue={setValue}
                  selectHour={setWeekdaysArr}
                  days={schedules} 
                  currentTotalSchedules={currentTotalSchedules}
                />
              </MotionGrid>
            </MotionGrid>
            <MotionGrid
              item
              xs={12} 
              md={3} 
              sx={{
                display: "flex",
                alignItems: "center", 
                justifyContent: "flex-start", 
                flexDirection: "column",
                p:1,
                gap: 2 // Added gap between children
              }}
            >
              <ListSelectCustom listName={listName} setListName={setListName} />
              <TimeZoneList value={timeZone} onChange={handleSelectTimeZone} />
            </MotionGrid>

            <MotionGrid
              item
              xs={12} 
              md={9} 
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                p: 2,
                pt: {xs:1, md:3}, 
                pr: {xs:2, md:0} 
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {xs: "column", sm: "row"}, 
                  alignItems: "stretch", 
                  justifyContent: "center",
                  mb: 3,
                  width: "100%",
                  gap: 1 
                }}
              >
                <TextField
                  fullWidth
                  label="Recipient Email"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <FormControl sx={{ width: "30%" }}>
                  <InputLabel id="demo-simple-select-label">
                    File extension
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={fileType}
                    label="File extension"
                    onChange={(e) => setFileType(e.target.value)}
                  >
                    <MenuItem value="xlsx">XLSX</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <Fab
                  disabled={btnValidation}
                  sx={{
                    backgroundColor: "#008c23",
                    color: "white",
                    "&:hover": { backgroundColor: "#008c2390" },
                  }}
                  aria-label="save"
                  onClick={handleSaveSchedule}
                >
                  <SaveIcon />
                </Fab>
                <Typography variant="button" sx={{ mt: 2 }}>
                  Save Schedule
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                {currentTotalSchedules > maxSchedules && (
                  <Typography color="error">
                    You have exceeded the maximum limit of schedules.
                  </Typography>
                )}
              </Box>
            </MotionGrid>
          </MotionGrid>
        )}
      </AnimatePresence>
    </>
  );
}
